import Decimal from 'decimal.js';

import type { Dollars } from 'models/dollars';

/*
  A simple function to remove degenerate prices and round to the next price that makes sense.
  It guarantees prices are always between 1 and 99.
  This function is used to guarantee which sync our graphs with the degenerate prices we have on
  the stats history api.
 */
export const isValidPrice = (priceInCents?: number): boolean => {
    return !!priceInCents && 0 < priceInCents && priceInCents < 100;
};

export const isValidPriceDollars = (priceDollars?: Dollars): boolean => {
    return !!priceDollars && priceDollars.gt(0) && priceDollars.lt(1);
};

export const closestValidPrice = (priceInCents: number): number => {
    return Math.max(Math.min(priceInCents, 99), 1);
};

/**
 * Substitutes for a degenerate price. 1c/99c are the only endpoints on every
 * `PriceLevelStructure`'s tick grid at once — `linear_cent` ticks at 1c and
 * `center_half_edge_half_cent` at 0.5c, so a 0.1c substitute would land
 * off-grid on both and be rejected just like the price it replaced.
 */
const MIN_SUBSTITUTE_PRICE_DOLLARS = new Decimal('0.01');
const MAX_SUBSTITUTE_PRICE_DOLLARS = new Decimal('0.99');

/**
 * Dollars analogue of `closestValidPrice` for prices derived from book levels.
 * Replaces a price `isValidPriceDollars` would reject, so a malformed 0c or
 * 100c level can no longer produce the `1.0000`/`0.0000` price the exchange
 * must reject.
 *
 * Anything in range passes through UNTOUCHED — it came off a book level, so it
 * already sits on the market's tick grid, and rounding it to a whole cent would
 * destroy the legitimate 0.1c/99.9c ticks such a price is usually built from.
 * Only the degenerate ends move, where a valid tick matters and precision does
 * not. Mirrors `normalizeFallbackAskCents`, which does the same in cents.
 */
export const closestValidPriceDollars = (priceDollars: Dollars): Dollars => {
    if (priceDollars.lte(0)) return MIN_SUBSTITUTE_PRICE_DOLLARS;
    if (priceDollars.gte(1)) return MAX_SUBSTITUTE_PRICE_DOLLARS;
    return priceDollars;
};

export const calculateSpread = (yesBid: number, yesAsk: number): number => {
    return Math.abs(yesAsk - yesBid);
};
